import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FServiceAPI } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';
import { handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2 } from '@/utils/service';
import { IResourceCreateVersionDraftType } from '@/type/resourceTypes';
import type {
  ChangeAction,
  OnSucceed_step2_localUpload_Action,
  OnSucceed_step2_storageSpace_Action,
  OnClick_step2_editMarkdownBtn_Action,
  OnClose_step2_editMarkdown,
  OnClick_step2_editCartoonBtn_Action,
  OnClose_step2_editCartoon_Action,
  OnRemove_step2_file_Action,
  OnChange_step2_systemProperties_Action,
  OnChange_step2_customProperties_Action,
  OnChange_step2_customConfigurations_Action,
  OnClick_step2_submitBtn_Action,
  OnTrigger_step2_SaveDraft_Action,
  ResourceCreatorPageModelState,
} from './types';

export const step2Effects = {
  *onSucceed_step2_localUpload(
    { payload }: OnSucceed_step2_localUpload_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_fileInfo: {
          name: payload.fileName,
          sha1: payload.sha1,
          from: '本地上传',
        },
        step2_videoCover: '',
        step2_systemPropertiesState: 'parsing',
        step2_dataIsDirty_count: resourceCreatorPage.step2_dataIsDirty_count + 1,
      },
    });

    const params4: Parameters<
      typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2
    >[0] = {
      sha1: payload.sha1,
      resourceTypeCode: resourceCreatorPage.step1_createdResourceInfo?.resourceTypeCode || '',
      inheritData: {
        additionalProperties: getPersistedAdditionalProperties(resourceCreatorPage),
        customProperties: [],
        customConfigurations: [],
      },
    };

    const result: Awaited<
      ReturnType<typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2>
    > = yield call(handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2, params4);

    if (result.state !== 'success') {
      fMessage(result.failedMsg, 'error');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_systemProperties: result.systemProperties,
        step2_draftAdditionalProperties: getAdditionalPropertiesFromSystemProperties(
          result.systemProperties,
        ),
        step2_customProperties: result.customProperties,
        step2_customConfigurations: result.customConfigurations,
        step2_systemPropertiesState: 'success',
      },
    });
  },
  *onSucceed_step2_storageSpace(
    { payload }: OnSucceed_step2_storageSpace_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_fileInfo: {
          name: payload.objectName,
          sha1: payload.sha1,
          from: '存储空间',
        },
        step2_videoCover: '',
        step2_systemPropertiesState: 'parsing',
      },
    });

    const params4: Parameters<
      typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2
    >[0] = {
      sha1: payload.sha1,
      resourceTypeCode: resourceCreatorPage.step1_createdResourceInfo?.resourceTypeCode || '',
      inheritData: {
        additionalProperties: getPersistedAdditionalProperties(resourceCreatorPage),
        customProperties: [],
        customConfigurations: [],
      },
    };

    const result: Awaited<
      ReturnType<typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2>
    > = yield call(handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2, params4);

    if (result.state !== 'success') {
      fMessage(result.failedMsg, 'error');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_systemProperties: result.systemProperties,
        step2_draftAdditionalProperties: getAdditionalPropertiesFromSystemProperties(
          result.systemProperties,
        ),
        step2_customProperties: result.customProperties,
        step2_customConfigurations: result.customConfigurations,
        step2_systemPropertiesState: 'success',
      },
    });
  },
  *onClick_step2_editMarkdownBtn(
    {}: OnClick_step2_editMarkdownBtn_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    if (!resourceCreatorPage.step1_createdResourceInfo?.resourceID) {
      return;
    }

    const params0: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
      resourceId: resourceCreatorPage.step1_createdResourceInfo.resourceID,
    };
    const {
      data: data_draft0,
    }: {
      data: null | {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.lookDraft, params0);

    if (!data_draft0) {
      const draftData: IResourceCreateVersionDraftType = {
        versionInput: '1.0.0',
        selectedFileInfo: null,
        additionalProperties: [],
        customProperties: [],
        customConfigurations: [],
        directDependencies: [],
        descriptionEditorInput: '',
        baseUpcastResources: [],
      };

      const params1: Parameters<typeof FServiceAPI.Resource.saveVersionsDraft>[0] = {
        resourceId: resourceCreatorPage.step1_createdResourceInfo.resourceID,
        draftData: draftData,
      };
      yield call(FServiceAPI.Resource.saveVersionsDraft, params1);
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_isOpenMarkdown: true,
      },
    });
  },
  *onClose_step2_editMarkdown(
    {}: OnClose_step2_editMarkdown,
    { select, put, call }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_isOpenMarkdown: false,
        step2_dataIsDirty_count: resourceCreatorPage.step2_dataIsDirty_count + 1,
      },
    });

    const params2: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
      resourceId: resourceCreatorPage.step1_createdResourceInfo?.resourceID || '',
    };
    const {
      data: data_draft2,
    }: {
      data: null | {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.lookDraft, params2);

    const draftData = data_draft2?.draftData;
    if (!draftData?.selectedFileInfo) {
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_fileInfo: draftData.selectedFileInfo,
        step2_videoCover: draftData.videoCover || '',
        step2_directDependencies: draftData.directDependencies,
        step2_baseUpcastResources: draftData.baseUpcastResources,
        step2_authReload: resourceCreatorPage.step2_authReload + 1,
        step2_systemPropertiesState: 'parsing',
      },
    });

    const params4: Parameters<
      typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2
    >[0] = {
      sha1: draftData.selectedFileInfo.sha1,
      resourceTypeCode: resourceCreatorPage.step1_resourceType?.value || '',
      inheritData: {
        additionalProperties: draftData.additionalProperties.map((ap) => {
          return {
            key: ap.key,
            name: '',
            value: ap.value,
            description: '',
          };
        }),
        customProperties: draftData.customProperties,
        customConfigurations: draftData.customConfigurations,
      },
    };
    const result: Awaited<
      ReturnType<typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2>
    > = yield call(handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2, params4);

    if (result.state !== 'success') {
      fMessage(result.failedMsg, 'error');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_systemPropertiesState: 'success',
        step2_systemProperties: result.systemProperties,
        step2_draftAdditionalProperties: getAdditionalPropertiesFromSystemProperties(
          result.systemProperties,
        ),
        step2_customProperties: result.customProperties,
        step2_customConfigurations: result.customConfigurations,
      },
    });
  },
  *onClick_step2_editCartoonBtn(
    {}: OnClick_step2_editCartoonBtn_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    if (!resourceCreatorPage.step1_createdResourceInfo?.resourceID) {
      return;
    }

    const params0: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
      resourceId: resourceCreatorPage.step1_createdResourceInfo.resourceID,
    };
    const {
      data: data_draft0,
    }: {
      data: null | {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.lookDraft, params0);

    if (!data_draft0) {
      const draftData: IResourceCreateVersionDraftType = {
        versionInput: '1.0.0',
        selectedFileInfo: null,
        additionalProperties: [],
        customProperties: [],
        customConfigurations: [],
        directDependencies: [],
        descriptionEditorInput: '',
        baseUpcastResources: [],
      };

      const params1: Parameters<typeof FServiceAPI.Resource.saveVersionsDraft>[0] = {
        resourceId: resourceCreatorPage.step1_createdResourceInfo.resourceID,
        draftData: draftData,
      };
      yield call(FServiceAPI.Resource.saveVersionsDraft, params1);
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_isOpenCartoon: true,
      },
    });
  },
  *onClose_step2_editCartoon(
    action: OnClose_step2_editCartoon_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_isOpenCartoon: false,
        step2_dataIsDirty_count: resourceCreatorPage.step2_dataIsDirty_count + 1,
      },
    });

    const params2: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
      resourceId: resourceCreatorPage.step1_createdResourceInfo?.resourceID || '',
    };
    const {
      data: data_draft2,
    }: {
      data: null | {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.lookDraft, params2);

    const draftData = data_draft2?.draftData;
    if (!draftData?.selectedFileInfo) {
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_fileInfo: draftData.selectedFileInfo,
        step2_videoCover: draftData.videoCover || '',
        step2_directDependencies: draftData.directDependencies,
        step2_baseUpcastResources: draftData.baseUpcastResources,
        step2_authReload: resourceCreatorPage.step2_authReload + 1,
        step2_systemPropertiesState: 'parsing',
      },
    });

    const params4: Parameters<
      typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2
    >[0] = {
      sha1: draftData.selectedFileInfo.sha1,
      resourceTypeCode: resourceCreatorPage.step1_resourceType?.value || '',
      inheritData: {
        additionalProperties: draftData.additionalProperties.map((ap) => {
          return {
            key: ap.key,
            name: '',
            value: ap.value,
            description: '',
          };
        }),
        customProperties: draftData.customProperties,
        customConfigurations: draftData.customConfigurations,
      },
    };
    const result: Awaited<
      ReturnType<typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2>
    > = yield call(handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2, params4);
    if (result.state !== 'success') {
      fMessage(result.failedMsg, 'error');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_systemPropertiesState: 'success',
        step2_systemProperties: result.systemProperties,
        step2_draftAdditionalProperties: getAdditionalPropertiesFromSystemProperties(
          result.systemProperties,
        ),
        step2_customProperties: result.customProperties,
        step2_customConfigurations: result.customConfigurations,
      },
    });
  },
  *onRemove_step2_file({}: OnRemove_step2_file_Action, { select, put }: EffectsCommandMap) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_fileInfo: null,
        step2_videoCover: '',
        step2_systemProperties: [],
        step2_systemPropertiesState: 'parsing',
        step2_draftAdditionalProperties: getPersistedAdditionalProperties(resourceCreatorPage),
        step2_customProperties: [],
        step2_customConfigurations: [],
        step2_dataIsDirty_count: resourceCreatorPage.step2_dataIsDirty_count + 1,
      },
    });
  },
  *onChange_step2_systemProperties(
    { payload }: OnChange_step2_systemProperties_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_systemProperties: payload.value,
        step2_dataIsDirty_count: resourceCreatorPage.step2_dataIsDirty_count + 1,
      },
    });
  },
  *onChange_step2_customProperties(
    { payload }: OnChange_step2_customProperties_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_customProperties: payload.value,
        step2_dataIsDirty_count: resourceCreatorPage.step2_dataIsDirty_count + 1,
      },
    });
  },
  *onChange_step2_customConfigurations(
    { payload }: OnChange_step2_customConfigurations_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_customConfigurations: payload.value,
        step2_dataIsDirty_count: resourceCreatorPage.step2_dataIsDirty_count + 1,
      },
    });
  },
  *onClick_step2_submitBtn(
    {}: OnClick_step2_submitBtn_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    if (!resourceCreatorPage.step1_createdResourceInfo || !resourceCreatorPage.step2_fileInfo) {
      return;
    }

    if (!resourceCreatorPage.step2_isCompleteAuthorization) {
      fMessage('依赖中存在未获取授权的资源', 'error');
      return;
    }
    const authExcludedItems: {
      resourceId: string;
      excludedType: 'contractId' | 'policyId';
      excludedValue: string;
    }[] = (resourceCreatorPage.step2_authExcludedItems || []).flatMap((r0) => {
      return r0.contracts.map((r1) => {
        return {
          resourceId: r0.resourceID,
          excludedType: 'contractId' as const,
          excludedValue: r1.contractID,
        };
      });
    });

    const params: Parameters<typeof FServiceAPI.Resource.createVersion>[0] = {
      resourceId: resourceCreatorPage.step1_createdResourceInfo.resourceID,
      version: '1.0.0',
      fileSha1: resourceCreatorPage.step2_fileInfo.sha1,
      filename: resourceCreatorPage.step2_fileInfo.name,
      // TODO 视频封面字段（接口就绪后传）：videoCover: resourceCreatorPage.step2_videoCover,
      baseUpcastResources: (resourceCreatorPage.step2_baseUpcastResources || []).map((r) => {
        return { resourceId: r.resourceID };
      }),
      dependencies: (resourceCreatorPage.step2_directDependencies || []).map((r) => {
        return {
          resourceId: r.id,
          versionRange: r.versionRange || '',
        };
      }),
      authExcludedItems: authExcludedItems,
      inputAttrs: resourceCreatorPage.step2_systemProperties
        .filter((sp) => sp.type === 'additional')
        .map((ap) => {
          return {
            key: ap.key,
            value: ap.value,
          };
        }),
      customPropertyDescriptors: [
        ...resourceCreatorPage.step2_customProperties.map<
          NonNullable<
            Parameters<typeof FServiceAPI.Resource.createVersion>[0]['customPropertyDescriptors']
          >[number]
        >((i) => {
          return {
            type: 'readonlyText',
            key: i.key,
            name: i.name,
            remark: i.description,
            defaultValue: i.value,
          };
        }),
        ...resourceCreatorPage.step2_customConfigurations.map<
          NonNullable<
            Parameters<typeof FServiceAPI.Resource.createVersion>[0]['customPropertyDescriptors']
          >[number]
        >((i) => {
          const isInput: boolean = i.type === 'input';
          const options: string[] = i.select;
          return {
            type: isInput ? 'editableText' : 'select',
            key: i.key,
            name: i.name,
            remark: i.description,
            defaultValue: isInput ? i.input : options[0],
            candidateItems: isInput ? undefined : options,
          };
        }),
      ],
      description: '',
    };

    const { ret, errCode, data, msg } = yield call(FServiceAPI.Resource.createVersion, params);

    if (ret !== 0 || errCode !== 0 || !data) {
      self._czc?.push(['_trackEvent', '版本发行页', '发行', '', 0]);
      fMessage(msg, 'error');
      return;
    }
    self._czc?.push(['_trackEvent', '版本发行页', '发行', '', 1]);

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step: 3,
        step2_dataIsDirty_count: 0,
      },
    });
  },
  *onTrigger_step2_SaveDraft(
    {}: OnTrigger_step2_SaveDraft_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );

    if (!resourceCreatorPage.step1_createdResourceInfo) {
      return;
    }

    const params1: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
      resourceId: resourceCreatorPage.step1_createdResourceInfo.resourceID,
    };
    const {
      data: data_draft1,
    }: {
      data: null | {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.lookDraft, params1);

    const draftData: IResourceCreateVersionDraftType = {
      ...(data_draft1?.draftData || {}),
      versionInput: '1.0.0',
      selectedFileInfo: resourceCreatorPage.step2_fileInfo,
      videoCover: resourceCreatorPage.step2_videoCover || '',
      additionalProperties: getPersistedAdditionalProperties(resourceCreatorPage),
      customProperties: resourceCreatorPage.step2_customProperties,
      customConfigurations: resourceCreatorPage.step2_customConfigurations,
      directDependencies: resourceCreatorPage.step2_directDependencies,
      baseUpcastResources: resourceCreatorPage.step2_baseUpcastResources,
      descriptionEditorInput: '',
    };

    const params: Parameters<typeof FServiceAPI.Resource.saveVersionsDraft>[0] = {
      resourceId: resourceCreatorPage.step1_createdResourceInfo.resourceID,
      draftData: draftData,
    };
    const {
      ret,
      errCode,
    }: {
      ret: number;
      errCode: number;
      data: {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.saveVersionsDraft, params);
    if (ret !== 0 || errCode !== 0) {
      fMessage('草稿保存失败', 'error');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step2_dataIsDirty_count: 0,
      },
    });
  },
};

function getAdditionalPropertiesFromSystemProperties(
  systemProperties: ResourceCreatorPageModelState['step2_systemProperties'],
): ResourceCreatorPageModelState['step2_draftAdditionalProperties'] {
  return systemProperties
    .filter((sp) => sp.type === 'additional')
    .map((sp) => {
      return {
        key: sp.key,
        value: sp.value,
      };
    });
}

function getPersistedAdditionalProperties(
  resourceCreatorPage: Pick<
    ResourceCreatorPageModelState,
    'step2_systemProperties' | 'step2_draftAdditionalProperties'
  >,
): ResourceCreatorPageModelState['step2_draftAdditionalProperties'] {
  const currentAdditionalProperties = getAdditionalPropertiesFromSystemProperties(
    resourceCreatorPage.step2_systemProperties,
  );

  return currentAdditionalProperties.length > 0
    ? currentAdditionalProperties
    : resourceCreatorPage.step2_draftAdditionalProperties;
}
